import { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';

const ViewAllServices = () => {
  const location = useLocation();
  const products = Array.isArray(location.state?.products) ? location.state.products : [];
  const navigate = useNavigate();

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const filteredProducts = products.filter((product) => {
    const productName = product?.name || '';
    const productDescription = product?.description || '';
    const matchesCategory = categoryFilter ? product.category?.name === categoryFilter : true;
    const matchesSearch = searchTerm
      ? productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      productDescription.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    return matchesCategory && matchesSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const uniqueCategories = [...new Set(products.map((product) => product.category?.name || 'Uncategorized'))];

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleView = (product) => {
    navigate('/view-service', { state: { primaryId: product.primaryId, product } });
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getImageUrl = (product) => {
    if (product.imageContents?.length > 0 && product.imageContents[0].displayLink) {
      return product.imageContents[0].displayLink;
    }
    return null;
  };

  return (
    <>
      <div className="py-16 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" style={{ paddingTop: '6rem' }}>
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center text-slate-800 mb-12">
            All Services
          </h2>

          {/* Filters Section */}
          <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 shadow-lg border border-slate-200 rounded-2xl mb-8 space-y-4 md:space-y-0">
            <div className="relative w-full md:w-1/3">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-4 pl-12 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 absolute left-3 top-3 text-slate-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            <div className="relative w-full md:w-1/4">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full p-4 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">All Categories</option>
                {uniqueCategories.map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {paginatedProducts.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                <h3 className="text-xl font-semibold text-slate-800">No services found</h3>
                <p className="mt-2 text-slate-600">Try adjusting the search or category filter.</p>
              </div>
            )}
            {paginatedProducts.map((product) => {
              const imageUrl = getImageUrl(product);
              return (
                <div
                  key={product.primaryId}
                  className="bg-white shadow-xl border border-slate-200 overflow-hidden rounded-2xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={product.name}
                      className="w-full h-56 object-cover rounded-t-2xl hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x250';
                      }}
                    />
                  ) : (
                    <div className="w-full h-56 bg-slate-200 flex items-center justify-center rounded-t-2xl">
                      <span className="text-slate-500 text-lg font-medium">{product.name}</span>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-800">{product.name}</h3>
                    <p className="text-slate-600 mt-2">{product.description || 'No description available'}</p>
                    <p className="text-lg font-bold text-slate-800 mt-4">
                      {product.price?.currency ? `${product.price.currency} ${product.price.priceAmt}` : `$${product.price?.priceAmt || '0'}`}
                    </p>
                    <p className="text-slate-600 mt-2">{product.category?.name || 'Category not specified'}</p>
                    <button
                      onClick={() => handleView(product)}
                      className="mt-4 w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="container mx-auto px-4 mt-8 flex justify-center space-x-4">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              <FiChevronLeft />
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewAllServices;
